import React, { useState } from 'react';

/** Stable identity tints so empty avatars aren't a wall of gray. Not status color. */
const TONES = [
  { bg: 'bg-[#E8F5E9] dark:bg-[#1C3128]', text: 'text-[#1B7A32] dark:text-[#A5D6A7]' },
  { bg: 'bg-[#E8EEF8] dark:bg-[#1A2433]', text: 'text-[#1D4E89] dark:text-[#90CAF9]' },
  { bg: 'bg-[#F3E8F6] dark:bg-[#2A1E30]', text: 'text-[#6A3A8A] dark:text-[#CE93D8]' },
  { bg: 'bg-[#FFF3E0] dark:bg-[#332414]', text: 'text-[#9A5B00] dark:text-[#FFCC80]' },
  { bg: 'bg-[#E0F2F1] dark:bg-[#14302E]', text: 'text-[#0F6B63] dark:text-[#80CBC4]' },
];

const SIZES = {
  sm: 'w-9 h-9 sm:w-10 sm:h-10 text-sm sm:text-base',
  md: 'w-11 h-11 text-base',
};

function hashSeed(value) {
  const str = String(value || '');
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = (h * 31 + str.charCodeAt(i)) >>> 0;
  }
  return h;
}

function clientPhotoUrl(client) {
  if (!client || typeof client !== 'object') return '';
  const raw =
    client.avatar_url
    || client.photo_url
    || client.profile_image
    || client.profile_picture
    || client.picture
    || client.avatar
    || '';
  const url = String(raw).trim();
  if (!url || url === 'null' || url === 'undefined') return '';
  return url;
}

function clientInitials(client) {
  const name = String(client?.full_name || client?.name || '').trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  const email = String(client?.email || '').trim();
  if (email) return email.slice(0, 2).toUpperCase();
  const code = String(client?.user_code || '').trim();
  if (code) return code.slice(0, 2).toUpperCase();
  return '?';
}

export default function ClientAvatar({
  client,
  size = 'md',
  pulseRingClass = '',
  pulseDotClass = '',
  ringOffsetClass = '',
  dotBorderClass = 'border-surface dark:border-canvas-dark',
}) {
  const photo = clientPhotoUrl(client);
  const [imgFailed, setImgFailed] = useState(false);
  const showPhoto = Boolean(photo) && !imgFailed;
  const tone = TONES[hashSeed(client?.id || client?.user_code || client?.email || client?.full_name) % TONES.length];
  const initials = clientInitials(client);
  const dim = SIZES[size] || SIZES.md;
  const label = client?.full_name || client?.email || 'Client';

  return (
    <div className="relative shrink-0">
      {showPhoto ? (
        <img
          src={photo}
          alt=""
          onError={() => setImgFailed(true)}
          className={`${dim} rounded-full object-cover ${pulseRingClass} ${ringOffsetClass}`}
        />
      ) : (
        <div
          aria-hidden
          className={`${dim} rounded-full flex items-center justify-center font-semibold tracking-wide ${tone.bg} ${tone.text} ${pulseRingClass} ${ringOffsetClass}`}
        >
          {initials}
        </div>
      )}
      {pulseDotClass ? (
        <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 ${dotBorderClass} ${pulseDotClass}`} />
      ) : null}
      <span className="sr-only">{label}</span>
    </div>
  );
}
